import { Card, CardBody } from "@/components/ui";

interface Lesson {
  slug: string;
  title: string;
  blurb: string;
  est_minutes: number;
  level: "beginner" | "intermediate" | "advanced";
}

const LESSONS: Lesson[] = [
  {
    slug: "leverage-basics",
    title: "What is leverage?",
    blurb: "Use of borrowed capital, margin maintenance, and how a 30:1 leverage cap protects retail traders.",
    est_minutes: 6,
    level: "beginner",
  },
  {
    slug: "spread-and-commission",
    title: "Spread, commission, and effective cost",
    blurb: "How brokers earn — the gap between bid and ask, plus per-lot fees. Why ECN and market-maker models price differently.",
    est_minutes: 7,
    level: "beginner",
  },
  {
    slug: "stop-loss",
    title: "Stops, take-profits, and trailing orders",
    blurb: "Hard stops, last-look protection, slippage, and the difference between a stop-loss and a stop-limit.",
    est_minutes: 8,
    level: "beginner",
  },
  {
    slug: "margin-call-stop-out",
    title: "Margin call vs. stop-out",
    blurb: "Why your equity matters more than your balance. Live walk-through of an MC at 50% / SO at 20% configuration.",
    est_minutes: 9,
    level: "intermediate",
  },
  {
    slug: "overnight-financing",
    title: "Overnight swap / financing",
    blurb: "Interest-rate differential, triple-swap Wednesdays, and reading your finance line on the daily statement.",
    est_minutes: 6,
    level: "intermediate",
  },
  {
    slug: "options-greeks",
    title: "Options greeks at a glance",
    blurb: "Delta, gamma, theta, vega — what each one means in dollar terms, plus a Black-Scholes intuition cheat sheet.",
    est_minutes: 12,
    level: "advanced",
  },
  {
    slug: "perpetual-funding",
    title: "Perpetual funding rates",
    blurb: "How crypto perpetual contracts converge to spot via 8h funding payments, and how to read them in your P&L.",
    est_minutes: 8,
    level: "advanced",
  },
  {
    slug: "appropriateness-test",
    title: "Why we ask the appropriateness test",
    blurb: "MIFID II Article 25(3) — knowledge / experience / financial-situation gate. How your score caps your max leverage.",
    est_minutes: 4,
    level: "beginner",
  },
];

export function AcademyPage() {
  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-base font-semibold text-term-fg">Academy</h1>
        <p className="text-2xs text-term-mute">
          Bite-sized lessons covering the trading concepts you'll meet in this
          platform. Browse by level — start with beginner if you're new to
          margined products. Each lesson is a 5–15 minute read.
        </p>
      </div>

      {(["beginner", "intermediate", "advanced"] as const).map((lvl) => {
        const items = LESSONS.filter((l) => l.level === lvl);
        return (
          <Card key={lvl}>
            <CardBody className="p-0">
              <div className="border-b border-term-border bg-term-elev px-3 py-1.5">
                <p className="text-2xs font-semibold uppercase text-term-mute">{lvl}</p>
              </div>
              <ul>
                {items.map((l) => (
                  <li
                    key={l.slug}
                    className="border-t border-term-border px-3 py-2 first:border-t-0"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm text-term-fg">{l.title}</p>
                        <p className="text-2xs text-term-mute">{l.blurb}</p>
                      </div>
                      <span className="shrink-0 text-2xs text-term-dim">
                        {l.est_minutes} min
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        );
      })}

      <p className="text-2xs text-term-mute">
        Lesson bodies are bundled with the frontend. Tenants can swap in their
        own CMS by replacing the Academy route handler.
      </p>
    </div>
  );
}
